import {SavedLegend, SavedLegendItem} from 'common/report/constants';
import {ColorChoice, FunnelSegment} from 'common/report/funnels/models/types';
import {colors} from 'common/util/colors';

const defaultColorPool = colors.charts.default.map((color) => color.primary);
const defaultOverallReservedColor = defaultColorPool[0];

function* cycle(arr: string[]) {
  while (true) {
    for (const item of arr) {
      yield item;
    }
  }
}

function emptyFrequencyMap(keys: string[]): Map<string, number> {
  return new Map(keys.map((k) => [k, 0] as [string, number]));
}

/**
 * Pick less used colors for new items.
 *
 * Assumes that the difference in used color frequencies is <= 1.
 */
export function maybeAssignColors(
  lastState: ColorChoice[],
  newItems: string[],
  colorPool: string[] = defaultColorPool,
  overallReservedColor: string = defaultOverallReservedColor,
): ColorChoice[] {
  const usedColorMap = new Map(
    // In case of older bookmarks, filter out any segment that
    // used the overallReservedColor from having its colors carry over.
    lastState
      .filter((c) => newItems.includes(c.key) && c.color !== overallReservedColor)
      .map((c) => [c.key, c.color] as [string, string]),
  );

  const usedColorFrequency = emptyFrequencyMap(colorPool.filter((item) => item !== overallReservedColor));
  usedColorMap.forEach((value) => {
    usedColorFrequency.set(value, usedColorFrequency.get(value) + 1);
  });

  const sortedColors = [...usedColorFrequency.keys()].sort((aColor, bColor) => {
    return usedColorFrequency.get(aColor) - usedColorFrequency.get(bColor);
  });

  const colors = cycle(sortedColors);
  return newItems.map((item) => {
    // Overall always gets the same color, the other segments are cycled
    if (item === `$overall`) {
      return {color: overallReservedColor, key: item};
    }
    const existing = usedColorMap.get(item);
    if (existing) {
      return {color: existing, key: item};
    } else {
      return {color: colors.next().value as string, key: item};
    }
  });
}

function getColorIndexForSegmentIndex(segmentIdx: number): number {
  return (segmentIdx % defaultColorPool.length) + 1;
}

/**
 * Convert selectedSegments to non-flattened saved legend. Used by Funnels chart types that use a data table for
 * segment selection.
 */
export function convertToFlatSavedLegend(colorChoices: ColorChoice[]): SavedLegend[] {
  const savedLegendItems = colorChoices.map((choice, segmentIdx) => {
    return {
      key: choice.key,
      color: getColorIndexForSegmentIndex(segmentIdx),
    };
  });

  return [{selected: savedLegendItems}];
}

/**
 * Converts old selected segments in the ColorChoice[] format to the new SavedLegend[] format for non-flat segments.
 * Used by Funnels TTC/Frequency bar charts.
 *
 * For example, if selectedSegments = ['Mac OS X / Chrome', 'Windows / Firefox']
 * Then the generated savedLegend will be structured like [['Mac OS X', 'Windows'], ['Chrome', 'Firefox']]
 * And the resulting bars that will be shown are:
 * ['Mac OS X / Chrome', 'Mac OS X / Firefox', 'Windows / Chrome', 'Windows / Firefox']
 */
export function convertToNonFlatSavedLegend(
  colorChoices: ColorChoice[],
  funnelSegments: FunnelSegment[],
): SavedLegend[] {
  // note: "$overall" is used raw instead of importing the OVERALL_PROP const because that creates an import dependency cycle
  const savedLegendArray = new Array<SavedLegend>();
  const segmentSets = new Array<Set<string>>();
  for (const choice of colorChoices) {
    if (choice.key !== `$overall`) {
      const choiceSplit = funnelSegments.find((funnelSeg) => funnelSeg.prop === choice.key)?.props;
      choiceSplit?.forEach((split, index) => {
        if (segmentSets[index] === undefined) {
          const newSet = new Set<string>();
          segmentSets.push(newSet);
        }
        segmentSets[index].add(split.prop);
      });
    }
  }

  const isOverallSelected = colorChoices.some((choice) => choice.key === `$overall`);
  if (isOverallSelected && segmentSets.length === 0) {
    segmentSets.push(new Set<string>());
  }

  for (const segmentSet of segmentSets) {
    const savedLegend: SavedLegend = {selected: new Array<SavedLegendItem>()};

    if (isOverallSelected) {
      savedLegend.selected.push({
        key: `$overall`,
        color: 0,
      });
    }

    const isFinalSegment = segmentSets[segmentSets.length - 1] === segmentSet;
    Array.from(segmentSet).forEach((segment, segmentIdx) => {
      const color = isFinalSegment ? getColorIndexForSegmentIndex(segmentIdx) : 1;
      const legendItem: SavedLegendItem = {key: segment, color};
      savedLegend.selected.push(legendItem);
    });
    savedLegendArray.push(savedLegend);
  }
  return savedLegendArray.length > 0 ? savedLegendArray : null;
}
